import React from "react";

import { UseCases } from "@planzone/core";
import { File } from "@planzone/dto";
import { events } from "@planzone/events";
import {
  useAuthentication,
  useChangeProfilePicture,
} from "@planzone/view-models";
import { Safeguard } from "@scenes/safeguard";
import { useMessage } from "@components/useMessage";
import { FileInput } from "@common/fileInput";
import { ResourceAvatar } from "@common/resourceAvatar";
import { ImageCropper } from "@common/imageCropper";
import { UploadIcon } from "./uploadIcon";
import { Loader } from "@common/loader";
import { useFilesCache } from "@components/cache";
import styles from "./styles.scss";

function ProfilePicture({ className }: { className: string }) {
  const {
    account: { resource },
  } = useAuthentication();
  const { changeProfilePicture, isRequestPending } = useChangeProfilePicture();
  const cache = useFilesCache();
  const croppedUri = React.useRef<string | null>(null);
  const [cropSource, setCropSource] = React.useState<{
    uri: string;
    name: string;
  } | null>(null);

  useMessage(
    React.useCallback(
      ({ type, data }) => {
        if (
          type === events.changeProfilePictureSucceeded &&
          croppedUri.current
        ) {
          const { profilePicture } = data;
          fetch(croppedUri.current)
            .then((res) => res.blob())
            .then((blob) => {
              cache.add(profilePicture.uuid, blob);
            });
          croppedUri.current = null;
        }
      },
      [cache],
    ),
  );

  return (
    <Safeguard useCases={[UseCases.changeProfilePicture]}>
      {({ canChangeProfilePicture }) => (
        <>
          <label className={styles.profilePicture}>
            <ResourceAvatar resource={resource} />
            {canChangeProfilePicture && (
              <FileInput
                className={styles.input}
                accepts={[
                  "image/jpeg",
                  "image/png",
                  "image/gif",
                  "image/bmp",
                  "image/webp",
                  "image/svg+xml",
                  "image/tiff",
                  "image/x-icon",
                  "image/heif",
                  "image/heic",
                  "image/vnd.microsoft.icon",
                  "image/x-ms-bmp",
                  "image/avif",
                  "image/jp2",
                  "image/jxr",
                ]}
                input={(file) =>
                  setCropSource({ uri: file.uri, name: file.name })
                }
              />
            )}
            {canChangeProfilePicture && (
              <div className={styles.overlay}>
                <UploadIcon className={styles.icon} />
              </div>
            )}
            {isRequestPending && (
              <div className={styles.loader}>
                <Loader />
              </div>
            )}
          </label>
          {cropSource && (
            <ImageCropper
              source={cropSource.uri}
              fileName={cropSource.name}
              onConfirm={(croppedFile: File) => {
                if (resource.avatarUrl) {
                  cache.remove(resource.avatarUrl);
                }
                croppedUri.current = croppedFile.uri;
                setCropSource(null);
                changeProfilePicture({ resource, profilePicture: croppedFile });
              }}
              onCancel={() => setCropSource(null)}
            />
          )}
        </>
      )}
    </Safeguard>
  );
}

export { ProfilePicture };
